import { Client, type Room } from "@colyseus/sdk";
import { useEffect, useState, type ReactNode } from "react";
import { useLanguage } from "./i18n";

export type PartyPlayerView = {
  id: string;
  name: string;
  score: number;
  connected: boolean;
};

export type RawPartyPlayerMap = {
  forEach(callback: (player: { id?: string; name?: string; score?: number; connected?: boolean }) => void): void;
};

export type ScoreboardView = {
  players: readonly PartyPlayerView[];
  winnerParticipantId?: string;
};

export function readPartyPlayers(raw: RawPartyPlayerMap | undefined): PartyPlayerView[] {
  const players: PartyPlayerView[] = [];
  raw?.forEach((player) => players.push({
    id: player.id ?? "",
    name: player.name ?? "",
    score: player.score ?? 0,
    connected: player.connected ?? false
  }));
  players.sort((left, right) => right.score - left.score || left.name.localeCompare(right.name));
  return players;
}

export type PartyRoomRecoveryHooks = {
  onDrop?: () => void;
  onReconnect?: () => void;
};

export function configurePartyRoomRecovery(room: Room): void {
  room.reconnection.enabled = true;
  room.reconnection.minUptime = 0;
  room.reconnection.maxRetries = 8;
  room.reconnection.delay = 100;
  room.reconnection.minDelay = 100;
  room.reconnection.maxDelay = 2_000;
  // Fast Buzzer actions are ephemeral: never replay a buzz sent while offline.
  room.reconnection.maxEnqueuedMessages = 0;
  room.reconnection.enqueuedMessages = [];
}

export async function connectPartyRoom(colyseusRoomId: string, ticket: string, onState: (state: unknown) => void, hooks: PartyRoomRecoveryHooks = {}): Promise<Room> {
  const endpoint = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`;
  const room = await new Client(endpoint).joinById(colyseusRoomId, { ticket });
  configurePartyRoomRecovery(room);
  room.onStateChange((state) => onState(state));
  room.onDrop(() => hooks.onDrop?.());
  room.onReconnect(() => {
    room.reconnection.enqueuedMessages = [];
    onState(room.state);
    hooks.onReconnect?.();
  });
  onState(room.state);
  return room;
}

export function useServerClock(active: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

export function secondsUntil(serverDeadline: number, clockOffset: number, now: number): number {
  if (!serverDeadline) return 0;
  return Math.max(0, Math.ceil((serverDeadline - (now + clockOffset)) / 1000));
}

export function ConnectionBadge({ state, children }: { state: string; children: ReactNode }) {
  return <span className={`connection ${state}`}>{children}</span>;
}

export function Leaderboard({ view, final = false }: { view: ScoreboardView; final?: boolean }) {
  const { tr } = useLanguage();
  return <section className="card wide"><h2>{final ? tr("Classifica finale", "Final leaderboard") : tr("Classifica", "Leaderboard")} <span className="muted">({view.players.filter((player) => player.connected).length} online)</span></h2><ol className="leaderboard">{view.players.map((player) => <li key={player.id} className={player.id === view.winnerParticipantId ? "winner" : ""}><span><i className={player.connected ? "online" : "offline"} />{player.name}</span><strong>{player.score}</strong></li>)}</ol>{view.players.length === 0 && <p>{tr("In attesa dei giocatori…", "Waiting for players…")}</p>}</section>;
}
